import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseWidget, NgCompInputs } from 'gridstack/dist/angular';
import { Observable } from 'rxjs';

import { ANALYZING_MODES, WIDGET_CATEGORIES } from '@shared/constants';
import {
  CommonResponseDTO,
  IBarWidgetData,
  IGroupBarWidgetResponse,
  IWidget,
  IWidgetEndpointResponse,
} from '@shared/interfaces';

import { PermissionFiltrationService } from '../../../../../services/permission-filtration.service';
import { SnackbarService } from '../../../../../services/snackbar.service';
import {
  LoggedUserService,
  UserInfoResponseDTO,
} from '../../../../auth/services';
import { ColorSchemeService } from '../../../../setup/appearance/services/color-scheme.service';
import { WidgetApiService } from '../../../services/widgets.api.service';

@Component({
  selector: 'app-group-bar-chart',
  templateUrl: 'group-bar-chart.component.html',
  styleUrls: ['group-bar-chart.component.scss'],
})
export class GroupBarChartComponent
  extends BaseWidget
  implements OnInit, OnChanges
{
  @Input() widget: IWidget;
  @Input() clickable: boolean;
  @Input() closable: boolean;

  result: IBarWidgetData[] = [];
  description: string;

  animations = true;
  view: [number, number] = [600, 200];
  xAxisLabel: string;
  yAxisLabel: string;
  barWidth = 200;

  loggedUserInfo: UserInfoResponseDTO;
  hasPermissions = false;
  isFailedToLoad = false;
  isLoading = false;
  darkMode = false;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private snackbar: SnackbarService,
    private widgetApiService: WidgetApiService,
    private permissionFiltrationService: PermissionFiltrationService,
    private colorSchemeService: ColorSchemeService,
    private loggedUserService: LoggedUserService
  ) {
    super();
  }

  onColorSchemeChange() {
    const currentColorScheme = this.colorSchemeService.currentActive();

    if (currentColorScheme === 'dark') {
      this.darkMode = true;
    } else {
      this.darkMode = false;
    }
  }

  public override serialize(): NgCompInputs | undefined {
    return this.widget ? { text: this.widget } : undefined;
  }

  ngOnInit(): void {
    console.log('comes here');
    this.onColorSchemeChange();
    this.isLoading = true;
    this.loggedUserService.dataStore.subscribe((data) => {
      this.loggedUserInfo = data;
      if (data) {
        this.refreshWidget();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.widget?.currentValue.endpoint) {
      this.isLoading = true;
      this.refreshWidget();
    }
  }

  onClickWidget() {
    switch (this.widget.category_key) {
      case WIDGET_CATEGORIES.LOGS:
        this.router.navigate([`/app/logs`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.CONNECTED_APPS:
        this.router.navigate([`/app/apps`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.CONNECTIONS:
        this.router.navigate([`/app/connections`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.DRIVES:
        this.router.navigate([`app/storage/drives`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.IDENTITIES:
        this.router.navigate([`/app/identities`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.KNOWLEDGEBASE:
        this.router.navigate([`/app/knowledgebase`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.MESSAGES:
        this.router.navigate([`/app/messages`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.MODULES:
        this.router.navigate([`/app/modules`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.NOTICES:
        this.router.navigate([`/app/notices`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.PERMISSIONS:
        this.router.navigate([`/app/authorization/permissions`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.PRIVILEGES:
        this.router.navigate([`/app/apps`], {
          queryParams: { is_search: true },
        });
        break;

      case WIDGET_CATEGORIES.ROLES:
        this.router.navigate([`/app/authorization/roles`], {
          queryParams: { is_search: true },
        });
        break;
      case WIDGET_CATEGORIES.TARGETS:
        this.router.navigate([`/app/targets`], {
          queryParams: { isSearch: true },
        });
        break;
      case WIDGET_CATEGORIES.ACHIEVEMENTS:
        this.router.navigate([`/app/achievements`], {
          queryParams: { isSearch: true },
        });
        break;
      case WIDGET_CATEGORIES.PAYOUTS:
        this.router.navigate([`/app/payouts`], {
          queryParams: { isSearch: true },
        });
        break;

      default:
        this.router.navigate([`/app/launchpad`]);
        break;
    }
  }

  refreshWidget() {
    this.isLoading = true;
    this.hasPermissions = this.permissionFiltrationService.validatePermissions({
      analyzingMode: ANALYZING_MODES.EVERY,
      permissions: this.widget.permissions,
    });
    let subscription: Observable<CommonResponseDTO<IWidgetEndpointResponse>>;
    if (this.widget.config) {
      subscription = this.widgetApiService.getConfigTypeWidgetData(
        this.widget.endpoint,
        this.widget.config
      );
    } else {
      subscription = this.widgetApiService.getWidgetData(this.widget.endpoint);
    }
    subscription.subscribe({
      next: (res: CommonResponseDTO<IGroupBarWidgetResponse>) => {
        this.result = res.data.data;
        this.view = [
          this.result.length * this.barWidth > 600
            ? this.result.length * this.barWidth
            : 600,
          200,
        ];
        this.description = res.data.description;
        this.xAxisLabel = res.data.xAxisLabel;
        this.yAxisLabel = res.data.yAxisLabel;
        this.isLoading = false;
        this.isFailedToLoad = false;
      },
      error: () => {
        this.isFailedToLoad = true;
        this.isLoading = false;
        this.snackbar.error(
          this.translate.instant('widgets.count-widget.failed-to-load-widget')
        );
      },
    });
  }
}
